import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Solicită ofertă către FDP" CTA.
 * Available to ANY logged-in user. Optionally attaches a sim_id when invoked
 * from a simulation page so sales can see the exact context.
 *
 * Props:
 *   - simulationId?: string
 *   - variant?: 'primary' | 'outline' (default 'primary')
 *   - size?: 'default' | 'sm' (default 'default')
 *   - className?: string
 *   - testId?: string
 */
export default function SalesQuoteButton({
  simulationId,
  variant = "primary",
  size = "default",
  className = "",
  testId = "sales-quote-btn",
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!user) return null; // hidden for anonymous users
  // Hidden for staff roles — admin and sales don't request quotes from themselves
  if (user.role === "admin" || user.role === "sales") return null;

  const submit = async () => {
    setSending(true);
    try {
      const body = { message: message.trim() || null };
      if (simulationId) body.simulation_id = simulationId;
      await api.post("/sales/quote-request", body);
      toast.success(t("quote.toast_success"), { duration: 8000 });
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || t("quote.toast_error"),
      );
    } finally {
      setSending(false);
    }
  };

  const btnClass =
    variant === "primary"
      ? "bg-orange-500 hover:bg-orange-600 text-white"
      : "border border-orange-500/40 text-orange-400 hover:bg-orange-500/10";

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className={`${btnClass} ${className}`}
        size={size}
        data-testid={testId}
      >
        <Mail size={size === "sm" ? 12 : 14} className="mr-1.5" />
        {t("quote.btn")}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !sending && setOpen(v)}>
        <DialogContent
          className="bg-zinc-950 border-white/10 max-w-lg"
          data-testid="sales-quote-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail size={18} className="text-orange-400" />
              {t("quote.title")}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t("quote.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded border border-white/10 bg-zinc-900/50 p-3 text-xs space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                {t("quote.your_data_label")}
              </div>
              <div className="text-zinc-300" data-testid="quote-dialog-email">
                <strong>{t("quote.field_email")}</strong> {user.email}
              </div>
              {user.name && (
                <div className="text-zinc-300" data-testid="quote-dialog-name">
                  <strong>{t("quote.field_name")}</strong> {user.name}
                </div>
              )}
              {user.phone && (
                <div className="text-zinc-300" data-testid="quote-dialog-phone">
                  <strong>{t("quote.field_phone")}</strong> {user.phone}
                </div>
              )}
              {user.company && (
                <div className="text-zinc-300" data-testid="quote-dialog-company">
                  <strong>{t("quote.field_company")}</strong> {user.company}
                </div>
              )}
              {simulationId && (
                <div className="text-emerald-400 mt-2 text-[11px]">
                  {t("quote.attached_sim")}
                </div>
              )}
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider mb-1 block">
                {t("quote.msg_label")}
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("quote.msg_placeholder")}
                maxLength={2000}
                rows={4}
                className="bg-zinc-900 border-white/10 text-white resize-none"
                data-testid="quote-dialog-message"
              />
              <div className="text-[10px] text-zinc-500 text-right mt-1">
                {message.length}/2000
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed border-t border-white/5 pt-3">
              {t("quote.consent_1")}{" "}
              <strong className="text-zinc-300">WEASEL ART SRL</strong>{" "}
              {t("quote.consent_2")}{" "}
              <a
                href="/politica-confidentialitate"
                target="_blank"
                rel="noreferrer"
                className="text-orange-400 hover:text-orange-300 underline"
                data-testid="quote-privacy-link"
              >
                {t("quote.consent_link")}
              </a>
              .
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={sending}
              className="text-zinc-400 hover:text-white"
              data-testid="sales-quote-cancel"
            >
              {t("quote.cancel")}
            </Button>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="quote-dialog-submit"
            >
              {sending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Send size={14} className="mr-1.5" />
              )}
              {t("quote.submit_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
